// PanelController
// Manages editor panel toggles: Preview, TOC, JSON

import { MarkdownPreview } from './MarkdownPreview';
import { TableOfContents } from './TableOfContents';
import { JsonPanel } from './JsonPanel';

export interface PanelCallbacks {
  getEditorContent: () => string;
  setEditorContent: (content: string) => void;
  scrollEditorToLine: (line: number) => void;
  hasCurrentDoc: () => boolean;
  onEditorChange: (content: string) => void;
  onPreviewEnabled?: () => void;
}

export class PanelController {
  private preview: MarkdownPreview;
  private toc: TableOfContents;
  private jsonPanel: JsonPanel;
  private callbacks: PanelCallbacks;

  private previewEnabled: boolean = false;
  private tocEnabled: boolean = false;
  private jsonPanelEnabled: boolean = false;

  constructor(
    preview: MarkdownPreview,
    toc: TableOfContents,
    jsonPanel: JsonPanel,
    callbacks: PanelCallbacks
  ) {
    this.preview = preview;
    this.toc = toc;
    this.jsonPanel = jsonPanel;
    this.callbacks = callbacks;

    // Load preferences
    this.previewEnabled = localStorage.getItem('notepad_preview_enabled') === 'true';
    this.tocEnabled = localStorage.getItem('notepad_toc_enabled') === 'true';
    this.jsonPanelEnabled = localStorage.getItem('notepad_json_enabled') === 'true';
  }

  isPreviewEnabled(): boolean {
    return this.previewEnabled;
  }

  isTocEnabled(): boolean {
    return this.tocEnabled;
  }

  isJsonPanelEnabled(): boolean {
    return this.jsonPanelEnabled;
  }

  /**
   * Apply initial panel states (call after editor init)
   */
  applyInitialState(): void {
    if (this.previewEnabled) {
      this.togglePreview(true);
    }
    if (this.tocEnabled) {
      this.toggleToc(true);
    }
    if (this.jsonPanelEnabled) {
      this.toggleJsonPanel(true);
    }
  }

  /**
   * Update panels with current content
   */
  updatePanels(content: string): void {
    if (this.previewEnabled) {
      this.preview.update(content);
    }
    if (this.tocEnabled) {
      this.toc.update(content);
    }
  }

  /**
   * Scroll preview to line (for cursor sync)
   */
  scrollPreviewToLine(line: number, totalLines: number): void {
    if (this.previewEnabled) {
      this.preview.scrollToLine(line, totalLines);
    }
  }

  togglePreview(forceState?: boolean): void {
    const container = document.getElementById('editor-container')!;
    const btn = document.getElementById('btn-preview');

    this.previewEnabled = forceState !== undefined ? forceState : !this.previewEnabled;

    if (this.previewEnabled) {
      container.classList.add('split-view');
      this.preview.show();
      btn?.classList.add('active');

      if (this.callbacks.hasCurrentDoc()) {
        const content = this.callbacks.getEditorContent();
        this.preview.update(content);
      }

      // Notify App to auto-open info panel
      this.callbacks.onPreviewEnabled?.();
    } else {
      container.classList.remove('split-view');
      this.preview.hide();
      btn?.classList.remove('active');
    }

    localStorage.setItem('notepad_preview_enabled', String(this.previewEnabled));
  }

  toggleToc(forceState?: boolean): void {
    const btn = document.getElementById('btn-toc');

    this.tocEnabled = forceState !== undefined ? forceState : !this.tocEnabled;

    if (this.tocEnabled) {
      this.toc.show();
      btn?.classList.add('active');

      this.toc.onItemClick((line) => {
        this.callbacks.scrollEditorToLine(line);
      });

      if (this.callbacks.hasCurrentDoc()) {
        const content = this.callbacks.getEditorContent();
        this.toc.update(content);
      }
    } else {
      this.toc.hide();
      btn?.classList.remove('active');
    }

    localStorage.setItem('notepad_toc_enabled', String(this.tocEnabled));
  }

  toggleJsonPanel(forceState?: boolean): void {
    const btn = document.getElementById('btn-json');

    this.jsonPanelEnabled = forceState !== undefined ? forceState : !this.jsonPanelEnabled;

    if (this.jsonPanelEnabled) {
      this.jsonPanel.show();
      btn?.classList.add('active');

      this.jsonPanel.onInsertToEditor((json) => {
        if (this.callbacks.hasCurrentDoc()) {
          const content = this.callbacks.getEditorContent();
          const codeBlock = '```json\n' + json + '\n```';
          const newContent = content + (content.endsWith('\n') ? '' : '\n') + '\n' + codeBlock;
          this.callbacks.setEditorContent(newContent);
          this.callbacks.onEditorChange(newContent);
        }
      });
    } else {
      this.jsonPanel.hide();
      btn?.classList.remove('active');
    }

    localStorage.setItem('notepad_json_enabled', String(this.jsonPanelEnabled));
  }
}
